import { useEffect, useRef, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { AntiMetalButton } from '../components/ui/anti-metal-button';
import './GrowthServices.css';

const PILLARS = [
  {
    id: 'digital',
    num: '01',
    label: 'Digital Experiences',
    headline: 'Websites built to do more than look good.',
    body: 'We design and develop digital experiences around how people discover, evaluate and decide.',
    tags: [
      'Custom Website Development',
      'Shopify Development',
      'Website Revamps',
      'UX & UI',
      'Landing Pages',
      'Headless & Custom Builds',
    ],
    accent: '#ec2b28',
  },
  {
    id: 'conversion',
    num: '02',
    label: 'Conversion',
    headline: 'Make existing demand work harder.',
    body: 'Before increasing spend, we look for the friction already costing you customers.',
    tags: [
      'Conversion Rate Optimisation',
      'Funnel Analysis',
      'Product Page Optimisation',
      'Checkout Optimisation',
      'User Journey Analysis',
      'Landing Page Optimisation',
    ],
    accent: '#f97316',
  },
  {
    id: 'acquisition',
    num: '03',
    label: 'Acquisition',
    headline: 'Find the channels worth scaling.',
    body: 'We plan, launch and optimise campaigns around meaningful business outcomes — not just reach and impressions.',
    tags: [
      'Paid Media',
      'Performance Marketing',
      'Search Advertising',
      'Social Advertising',
      'LinkedIn Campaigns',
      'Campaign Strategy',
    ],
    accent: '#3b82f6',
  },
  {
    id: 'organic',
    num: '04',
    label: 'Organic Growth',
    headline: 'Build visibility that compounds.',
    body: 'Long-term organic strategies that build authority, reduce dependency on paid, and keep compounding over time.',
    tags: [
      'SEO Strategy',
      'Technical SEO',
      'Content Strategy',
      'On-Page Optimisation',
      'Search Performance',
      'Organic Growth Systems',
    ],
    accent: '#22c55e',
  },
];

function PillarCard({ pillar, index }) {
  const cardRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className={`gs-card${visible ? ' gs-card--visible' : ''}`}
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      {/* Top row: number + label */}
      <div className="gs-card-top">
        <span className="gs-card-num" style={{ color: pillar.accent }}>{pillar.num}</span>
        <span className="gs-card-label" style={{ color: pillar.accent, borderColor: `${pillar.accent}30`, background: `${pillar.accent}10` }}>
          {pillar.label}
        </span>
      </div>

      {/* Divider */}
      <div className="gs-card-rule" style={{ background: `linear-gradient(90deg, ${pillar.accent}, transparent)` }} />

      {/* Headline */}
      <h3 className="gs-card-headline">{pillar.headline}</h3>

      {/* Body */}
      <p className="gs-card-body">{pillar.body}</p>

      {/* Tags */}
      <div className="gs-tags">
        {pillar.tags.map((tag) => (
          <span key={tag} className="gs-tag" style={{ borderColor: `${pillar.accent}25`, color: 'rgba(255,255,255,0.65)' }}>
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function GrowthServices() {
  const sectionRef = useRef(null);

  // Scroll-triggered blur reveal for the header
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('gs-section--visible'); },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="gs-section" id="growth-services" ref={sectionRef}>
      {/* Section header */}
      <div className="gs-header">
        <p className="gs-eyebrow">What We Do</p>
        <h2 className="gs-heading">
          Growth that covers<br />
          <span className="gs-heading-accent">the full path.</span>
        </h2>
        <p className="gs-subhead">
          From the first click to the final conversion — four interconnected disciplines,
          one integrated system.
        </p>
      </div>

      {/* 2×2 grid of pillars */}
      <div className="gs-grid">
        {PILLARS.map((pillar, i) => (
          <PillarCard key={pillar.id} pillar={pillar} index={i} />
        ))}
      </div>

      {/* CTA */}
      <div className="gs-cta">
        <AntiMetalButton as="a" href="https://calendly.com/aurelixlabs/30min" target="_blank" rel="noopener noreferrer">
          Explore Aurelix Growth
          <ArrowUpRight size={16} style={{ marginLeft: 4 }} />
        </AntiMetalButton>
      </div>
    </section>
  );
}
